import React, { useState, useEffect } from 'react';
import axios from '../utils/api';
import ContainerEditor from './ContainerEditor';
import { ContainerComponentRule, ContainerTemplate } from '../types/containerTemplates';
import { TemplateContainer } from '../types/template';
import './ContainerManager.css';

interface ContainerManagerProps {
  onSave?: (container: TemplateContainer | ContainerTemplate) => void;
}

type TemplateContainerRow = Omit<TemplateContainer, 'template_id'> & {
  template_name?: string;
  template_id?: number | null;
  html_template_name?: string | null;
};

type AxiosErrorLike = {
  response?: {
    data?: {
      error?: string;
    };
  };
};

export function getApiErrorMessage(error: unknown, fallback: string): string {
  const maybe = error as AxiosErrorLike;
  return maybe?.response?.data?.error || fallback;
}

const getAuthRequestConfig = () => {
  const token = localStorage.getItem('token');
  return { headers: { Authorization: `Bearer ${token}` } };
};

export const getAreaDisplayName = (area?: string) => {
  switch ((area || '').toLowerCase()) {
    case 'header':
      return 'Шапка';
    case 'footer':
      return 'Подвал';
    case 'column':
      return 'Колонка';
    case 'sidebar':
      return 'Сайдбар';
    case 'menu':
      return 'Меню';
    case 'global':
      return 'Глобальный блок';
    case 'main':
    default:
      return 'Основная область';
  }
};

export const getScopeDisplayName = (scope: string) => {
  switch (scope) {
    case 'column':
      return 'Колонки';
    case 'universal':
      return 'Универсальные';
    case 'primary':
    default:
      return 'Основные блоки';
  }
};

type ContainerManagerHeaderProps = {
  onRefresh: () => void;
};

const ContainerManagerHeader: React.FC<ContainerManagerHeaderProps> = ({ onRefresh }) => {
  return (
    <div className="u2-row" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
      <div>
        <h2 className="u2-title" style={{ margin: 0 }}>Контейнеры</h2>
        <div className="u2-muted" style={{ fontSize: 12, marginTop: 4 }}>
          Управление экземплярами контейнеров и правилами компонентов.
        </div>
      </div>
      <div className="u2-toolbarGroup">
        <button type="button" className="u2-btn is-secondary" onClick={onRefresh}>
          Обновить
        </button>
      </div>
    </div>
  );
};

const ContainerManager: React.FC<ContainerManagerProps> = ({ onSave }) => {
  // Состояния для данных
  // 🆕 #F-114: containerTemplates оставлено для совместимости, но не используется (шаблоны скрыты)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [containerTemplates, setContainerTemplates] = useState<ContainerTemplate[]>([]);
  const [templateContainers, setTemplateContainers] = useState<TemplateContainerRow[]>([]);
  const [containerRules, setContainerRules] = useState<Record<number, ContainerComponentRule[]>>({});
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [availableComponents, setAvailableComponents] = useState<unknown[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Состояния для UI
  // 🆕 #F-114: Убрана вкладка "Шаблоны контейнеров", показываем только экземпляры
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  
  // Состояния для модальных окон
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [showTemplateEditor, setShowTemplateEditor] = useState(false);
  const [showContainerEditor, setShowContainerEditor] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [showCreateTemplate, setShowCreateTemplate] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [editingTemplate, setEditingTemplate] = useState<ContainerTemplate | null>(null);
  const [editingContainer, setEditingContainer] = useState<TemplateContainerRow | null>(null);

  // Загрузка данных при монтировании
  // 🆕 #F-114: Убрана загрузка шаблонов контейнеров, они не используются
  useEffect(() => {
    loadTemplateContainers();
    loadAvailableComponents();
    // Нам важно не менять поведение (загрузка при монтировании),
    // поэтому оставляем зависимости как раньше.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Загрузка правил для контейнеров при изменении списка контейнеров
  useEffect(() => {
    if (templateContainers.length > 0) {
      loadContainerRules();
    }
    // Нам важно не менять поведение (загрузка правил при изменении списка контейнеров),
    // поэтому оставляем зависимости как раньше.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateContainers]);

  // 🆕 #F-114: Функция не используется, шаблоны контейнеров скрыты в интерфейсе
  // const loadContainerTemplates = async () => {
  //   setLoading(true);
  //   try {
  //     const token = localStorage.getItem('token');
  //     const response = await axios.get('/api/admin/container-templates', {
  //       headers: { Authorization: `Bearer ${token}` }
  //     });
  //     setContainerTemplates(response.data);
  //   } catch (error) {
  //     console.error('Error loading container templates:', error);
  //     setError('Ошибка загрузки шаблонов контейнеров');
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const loadTemplateContainers = async () => {
    try {
      // Загружаем ВСЕ контейнеры (включая привязанные к шаблонам)
      const containersResponse = await axios.get('/api/admin/containers', getAuthRequestConfig());
      
      console.log('Загружено контейнеров (все):', containersResponse.data.length);
      
      // Устанавливаем все контейнеры
      setTemplateContainers((containersResponse.data as TemplateContainerRow[]).map((container) => ({
        ...container,
        template_name: container.html_template_name || 'Библиотека',
        template_id: container.template_id || null
      })));
      
    } catch (error) {
      console.error('Error loading template containers:', error);
      setError('Ошибка загрузки экземпляров контейнеров');
    }
  };

  const loadAvailableComponents = async () => {
    try {
      const response = await axios.get('/api/admin/components', getAuthRequestConfig());
      
      console.log('Загружено компонентов:', response.data.length);
      setAvailableComponents(response.data);
    } catch (error) {
      console.error('Error loading available components:', error);
    }
  };

  const loadContainerRules = async () => {
    try {
      const rules: Record<number, ContainerComponentRule[]> = {};
      
      // Загружаем правила для каждого контейнера
      for (const container of templateContainers) {
        if (container.id) {
          try {
            const response = await axios.get(`/api/admin/containers/${container.id}/component-rules`, getAuthRequestConfig());
            rules[container.id] = response.data as ContainerComponentRule[];
            console.log(`Загружено правил для контейнера ${container.name}:`, response.data.length);
          } catch (error) {
            console.log(`Правила для контейнера ${container.name} не найдены`);
            rules[container.id] = [];
          }
        }
      }
      
      setContainerRules(rules);
    } catch (error) {
      console.error('Error loading container rules:', error);
    }
  };

  // Фильтрация и поиск
  // 🆕 #F-114: Убрана фильтрация шаблонов, они не используются
  const filteredContainers = templateContainers.filter(container => {
    const matchesSearch = container.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         container.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = filterType === 'all' || container.container_type === filterType;
    return matchesSearch && matchesType;
  });

  // 🆕 #F-114: Обработчики для шаблонов контейнеров закомментированы, шаблоны не используются в интерфейсе
  // Эти функции оставлены для возможного будущего использования
  // const handleEditTemplate = (template: ContainerTemplate) => {
  //   setEditingTemplate(template);
  //   setShowTemplateEditor(true);
  // };

  // const handleDeleteTemplate = async (templateId: number) => {
  //   if (!window.confirm('Вы уверены, что хотите удалить этот шаблон контейнера?')) {
  //     return;
  //   }

  //   try {
  //     const token = localStorage.getItem('token');
  //     await axios.delete(`/api/admin/container-templates/${templateId}`, {
  //       headers: { Authorization: `Bearer ${token}` }
  //     });
  //     
  //     setContainerTemplates(prev => prev.filter(t => t.id !== templateId));
  //     console.log('✅ Шаблон контейнера удален');
  //   } catch (error: unknown) {
  //     console.error('Error deleting template:', error);
  //     setError(error.response?.data?.error || 'Ошибка удаления шаблона');
  //   }
  // };

  // const handleCreateTemplate = () => {
  //   setEditingTemplate(null);
  //   setShowCreateTemplate(true);
  // };

  // const handleTemplateCreated = (newTemplate: ContainerTemplate) => {
  //   setContainerTemplates(prev => [...prev, newTemplate]);
  //   setShowCreateTemplate(false);
  //   console.log('✅ Создан новый шаблон контейнера:', newTemplate.name);
  // };

  // const handleTemplateUpdated = (updatedTemplate: ContainerTemplate) => {
  //   setContainerTemplates(prev => 
  //     prev.map(t => t.id === updatedTemplate.id ? updatedTemplate : t)
  //   );
  //   setShowTemplateEditor(false);
  //   setEditingTemplate(null);
  //   console.log('✅ Шаблон контейнера обновлен:', updatedTemplate.name);
  // };

  // Обработчики для экземпляров контейнеров
  const handleEditContainer = (container: TemplateContainerRow) => {
    setEditingContainer(container);
    setShowContainerEditor(true);
  };

  const handleDeleteContainer = async (container: TemplateContainerRow) => {
    if (!window.confirm(`Вы уверены, что хотите удалить контейнер "${container.name}"?\n\nВнимание: Это также удалит все правила компонентов для этого контейнера.`)) {
      return;
    }

    try {
      await axios.delete(`/api/admin/containers/${container.id}`, getAuthRequestConfig());

      // Обновляем список контейнеров
      loadTemplateContainers();
      
      console.log(`✅ Контейнер "${container.name}" успешно удален`);
    } catch (error: unknown) {
      console.error('Error deleting container:', error);
      alert(getApiErrorMessage(error, 'Ошибка удаления контейнера'));
    }
  };

  const handleContainerUpdated = (updatedContainer: TemplateContainerRow) => {
    setTemplateContainers(prev =>
      prev.map(c => c.id === updatedContainer.id ? updatedContainer : c)
    );
    setShowContainerEditor(false);
    setEditingContainer(null);
    console.log('✅ Контейнер обновлен:', updatedContainer.name);
  };

  // Получение типов для фильтра
  const containerTypes = ['all', 'header', 'footer', 'menu', 'dynamic', 'custom'];
  const getTypeDisplayName = (type: string) => {
    switch (type) {
      case 'all': return 'Все типы';
      case 'header': return 'Шапка сайта';
      case 'footer': return 'Подвал сайта';
      case 'menu': return 'Навигация';
      case 'dynamic': return 'Динамический контент';
      case 'custom': return 'Пользовательские';
      default: return type;
    }
  };

  if (loading) {
    return (
      <div className="container-manager">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Загрузка контейнеров...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-manager">
      <ContainerManagerHeader
        onRefresh={() => {
          loadTemplateContainers();
          loadAvailableComponents();
        }}
      />

      {error && (
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          {error}
          <button type="button" className="u2-iconBtn" onClick={() => setError(null)} aria-label="Закрыть">
            ×
          </button>
        </div>
      )}

      {/* Панель фильтров и поиска */}
      <div className="container-manager__filters">
        <div className="search-box">
          <input
            type="text"
            placeholder="Поиск по названию или описанию..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="u2-input"
          />
        </div>
        
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="u2-select"
        >
          {containerTypes.map(type => (
            <option key={type} value={type}>
              {getTypeDisplayName(type)}
            </option>
          ))}
        </select>
      </div>

      {/* Список экземпляров контейнеров */}
      {/* 🆕 #F-114: Убрана вкладка "Шаблоны контейнеров", показываем только экземпляры */}
      <div className="container-manager__instances">
        <div className="u2-tableWrap" aria-label="Контейнеры">
          <table className="u2-table">
            <thead>
              <tr>
                <th>Название</th>
                <th>Тип</th>
                <th>Шаблон</th>
                <th>Область</th>
                <th>Лимиты</th>
                <th>Компоненты</th>
                <th>Статус</th>
                <th style={{ width: 110, textAlign: 'right' }}>Действия</th>
              </tr>
            </thead>
            <tbody>
              {filteredContainers.length === 0 ? (
                <tr>
                  <td colSpan={8}>
                    <span className="u2-muted" style={{ fontSize: 12 }}>
                      Экземпляры контейнеров не найдены.
                    </span>
                  </td>
                </tr>
              ) : (
                filteredContainers.map(container => {
                  const rules = container.id ? containerRules[container.id] || [] : [];
                  const allowedComponents = rules.filter(rule => rule.is_allowed);
                  const requiredComponents = rules.filter(rule => rule.is_required);
                  const areaLabel = getAreaDisplayName(container.area_type);
                  const scopeList = (container.allowed_component_scope && container.allowed_component_scope.length > 0)
                    ? container.allowed_component_scope
                    : (areaLabel === 'Колонка' ? ['column', 'universal'] : ['primary', 'universal']);
                  const isPublished = container.is_published !== false;

                  return (
                    <tr key={`${container.template_id}-${container.id}`}>
                      <td>
                        <div className="u2-strong">{container.name}</div>
                        {container.description ? (
                          <div className="u2-muted" style={{ fontSize: 12, marginTop: 4 }}>
                            {container.description}
                          </div>
                        ) : null}
                      </td>
                      <td>
                        <span className="u2-pill">{getTypeDisplayName(container.container_type)}</span>
                      </td>
                      <td>
                        <span className="u2-muted" title={container.template_name}>
                          {container.template_name}
                        </span>
                      </td>
                      <td>
                        <div className="u2-row" style={{ justifyContent: 'flex-start', gap: 8, flexWrap: 'wrap' }}>
                          <span className="u2-pill">{areaLabel}</span>
                          {scopeList.map(scope => (
                            <span key={`${container.id}-${scope}`} className="u2-pill">
                              {getScopeDisplayName(scope)}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td>
                        <span className="u2-muted">
                          {container.min_components}-{container.max_components}
                        </span>
                      </td>
                      <td>
                        {rules.length === 0 ? (
                          <span className="u2-muted" style={{ fontSize: 12 }}>Правила не настроены</span>
                        ) : (
                          <div style={{ display: 'grid', gap: 6 }}>
                            {requiredComponents.length > 0 ? (
                              <div>
                                <span className="u2-muted" style={{ fontSize: 12 }}>Обязательные:</span>{' '}
                                {requiredComponents.slice(0, 3).map(rule => (
                                  <span key={rule.id} className="u2-pill is-bad" style={{ marginLeft: 6 }}>
                                    {rule.component_type}
                                  </span>
                                ))}
                                {requiredComponents.length > 3 ? (
                                  <span className="u2-pill" style={{ marginLeft: 6 }}>
                                    +{requiredComponents.length - 3}
                                  </span>
                                ) : null}
                              </div>
                            ) : null}

                            {allowedComponents.length > 0 ? (
                              <div>
                                <span className="u2-muted" style={{ fontSize: 12 }}>
                                  Разрешенные ({allowedComponents.length}):
                                </span>{' '}
                                {allowedComponents.slice(0, 3).map(rule => (
                                  <span key={rule.id} className="u2-pill is-ok" style={{ marginLeft: 6 }}>
                                    {rule.component_type}
                                  </span>
                                ))}
                                {allowedComponents.length > 3 ? (
                                  <span className="u2-pill" style={{ marginLeft: 6 }}>
                                    +{allowedComponents.length - 3}
                                  </span>
                                ) : null}
                              </div>
                            ) : null}
                          </div>
                        )}
                      </td>
                      <td>
                        <span className={`u2-pill ${isPublished ? 'is-ok' : 'is-warn'}`}>
                          {isPublished ? 'Опубликован' : 'Только админ'}
                        </span>
                        {container.requires_menu_component ? (
                          <div className="u2-muted" style={{ fontSize: 12, marginTop: 6 }}>
                            ⚠ Требует меню
                          </div>
                        ) : null}
                        {container.disallow_menu_components ? (
                          <div className="u2-muted" style={{ fontSize: 12, marginTop: 6 }}>
                            🚫 Меню запрещено
                          </div>
                        ) : null}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <div className="u2-toolbarGroup" style={{ justifyContent: 'flex-end' }}>
                          <button
                            type="button"
                            className="u2-iconBtn"
                            onClick={() => handleEditContainer(container)}
                            title="Редактировать контейнер и правила компонентов"
                            aria-label="Редактировать"
                          >
                            <svg className="u2-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M12 20h9" />
                              <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
                            </svg>
                          </button>
                          <button
                            type="button"
                            className="u2-iconBtn"
                            onClick={() => handleDeleteContainer(container)}
                            title="Удалить контейнер"
                            aria-label="Удалить"
                            style={{
                              borderColor: 'hsl(355 85% 58% / 0.24)',
                              background: 'hsl(355 85% 58% / 0.12)',
                              color: 'hsl(355 85% 74%)',
                            }}
                          >
                            <svg className="u2-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M3 6h18" />
                              <path d="M8 6V4h8v2" />
                              <path d="M19 6l-1 14H6L5 6" />
                              <path d="M10 11v6" />
                              <path d="M14 11v6" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {filteredContainers.length === 0 && (searchQuery || filterType !== 'all') ? (
          <div className="u2-muted" style={{ marginTop: 12, fontSize: 12 }}>
            Попробуйте изменить критерии поиска.
          </div>
        ) : null}
        </div>

      {/* Модальные окна */}
      {/* 🆕 #F-114: Убраны модальные окна для шаблонов контейнеров, они не используются */}
      {showContainerEditor && editingContainer && (
        <ContainerEditor
          containerId={editingContainer.id}
          onSave={handleContainerUpdated}
          onClose={() => {
            setShowContainerEditor(false);
            setEditingContainer(null);
          }}
        />
      )}
    </div>
  );
};

// Простой редактор шаблонов контейнеров
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ContainerTemplateEditor: React.FC<{
  template?: ContainerTemplate;
  onSave: (template: ContainerTemplate) => void;
  onClose: () => void;
}> = ({ template, onSave, onClose }) => {
  type ContainerType = ContainerTemplate['container_type'];
  type PlacementType = NonNullable<ContainerTemplate['placement_type']>;

  type ContainerTemplateFormData = {
    name: string;
    description: string;
    container_type: ContainerType;
    placement_type: PlacementType;
    min_components: number;
    max_components: number;
    icon: string;
    color: string;
    is_active: boolean;
  };

  const toContainerType = (value: string): ContainerType => {
    if (value === 'header' || value === 'footer' || value === 'menu' || value === 'dynamic' || value === 'custom') {
      return value;
    }
    return 'custom';
  };

  const toPlacementType = (value: string): PlacementType => {
    if (value === 'any' || value === 'main-only' || value === 'sidebar-only' || value === 'top' || value === 'bottom') {
      return value;
    }
    return 'any';
  };

  const [formData, setFormData] = useState<ContainerTemplateFormData>({
    name: template?.name || '',
    description: template?.description || '',
    container_type: template?.container_type || 'custom',
    placement_type: template?.placement_type || 'any',
    min_components: template?.min_components || 1,
    max_components: template?.max_components || 10,
    icon: template?.icon || 'default',
    color: template?.color || '#007bff',
    is_active: template?.is_active !== false
  });
  
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Название обязательно');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const url = template 
        ? `/api/admin/container-templates/${template.id}`
        : '/api/admin/container-templates';
      const method = template ? 'put' : 'post';

      const response = await axios[method](url, formData, getAuthRequestConfig());

      onSave(response.data);
    } catch (error: unknown) {
      console.error('Error saving template:', error);
      setError(getApiErrorMessage(error, 'Ошибка сохранения шаблона'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content template-editor">
        <div className="modal-header">
          <h2>{template ? 'Редактирование шаблона' : 'Создание шаблона'}</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        {error && (
          <div className="error-message">
            <span className="error-icon">⚠️</span>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="template-form">
          <div className="form-row">
            <div className="form-group">
              <label>Название: *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="Название шаблона"
                required
              />
            </div>
            <div className="form-group">
              <label>Тип контейнера:</label>
              <select
                value={formData.container_type}
                onChange={(e) => setFormData({...formData, container_type: toContainerType(e.target.value)})}
              >
                <option value="header">Шапка сайта</option>
                <option value="footer">Подвал сайта</option>
                <option value="menu">Навигация</option>
                <option value="dynamic">Динамический контент</option>
                <option value="custom">Пользовательский</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Описание:</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              placeholder="Описание назначения шаблона"
              rows={3}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Размещение:</label>
              <select
                value={formData.placement_type}
                onChange={(e) => setFormData({...formData, placement_type: toPlacementType(e.target.value)})}
              >
                <option value="any">Любое размещение</option>
                <option value="main-only">Только в основной области</option>
                <option value="sidebar-only">Только в боковых колонках</option>
                <option value="top">Только сверху</option>
                <option value="bottom">Только снизу</option>
              </select>
            </div>
            <div className="form-group">
              <label>Цвет:</label>
              <input
                type="color"
                value={formData.color}
                onChange={(e) => setFormData({...formData, color: e.target.value})}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Мин. компонентов:</label>
              <input
                type="number"
                min="0"
                max="50"
                value={formData.min_components}
                onChange={(e) => setFormData({...formData, min_components: parseInt(e.target.value)})}
              />
            </div>
            <div className="form-group">
              <label>Макс. компонентов:</label>
              <input
                type="number"
                min="1"
                max="50"
                value={formData.max_components}
                onChange={(e) => setFormData({...formData, max_components: parseInt(e.target.value)})}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Иконка:</label>
              <input
                type="text"
                value={formData.icon}
                onChange={(e) => setFormData({...formData, icon: e.target.value})}
                placeholder="default"
              />
            </div>
            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                />
                Активен
              </label>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Отмена
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Сохранение...' : 'Сохранить'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContainerManager;
